import { createApi } from '@reduxjs/toolkit/query/react';
import Employee from 'services/api/Employee';
import baseQuery from 'store/app/entities/baseQuery';

const initialState = {
  employees: [],
};

const findEmployeeInDraft = (draft, employeeId) => {
  if (!draft) {
    return null;
  }
  if (typeof draft.data?.find === 'function') {
    return draft.data.find(employee => employee._id === employeeId);
  }
  if (typeof draft.employees?.find === 'function') {
    return draft.employees.find(employee => employee._id === employeeId);
  }
  if (typeof draft.employee === 'object' && draft.employee?._id === employeeId) {
    return draft.employee;
  }
  if (Array.isArray(draft) && typeof draft.find === 'function') {
    return draft.find(employee => employee._id === employeeId);
  }
  return null;
};

// api is assigned after endpoints are defined (endpoints reference `api` for cache invalidation).
// eslint-disable-next-line prefer-const -- forward reference from endpoint handlers
let api;

export const findEmployeeInApiCaches = (getState, employeeId) => {
  if (!api || !employeeId) {
    return null;
  }
  const entries = api.util.selectInvalidatedBy(getState(), [{ type: 'Employee', _id: employeeId }]);
  for (const item of entries) {
    const draft = api.endpoints[item.endpointName]?.select(item.originalArgs)(getState())?.data;
    const found = findEmployeeInDraft(draft, employeeId);
    if (found) {
      return found;
    }
  }
  return null;
};

export const patchEmployeeInApiCaches = (dispatch, getState, employeeId, patch) => {
  if (!api || !employeeId || !patch || typeof patch !== 'object') {
    return;
  }
  const entries = api.util.selectInvalidatedBy(getState(), [{ type: 'Employee', _id: employeeId }]);
  entries.forEach(item => {
    dispatch(
      api.util.updateQueryData(item.endpointName, item.originalArgs, draft => {
        const found = findEmployeeInDraft(draft, employeeId);
        if (found) {
          Object.assign(found, patch);
        }
      }),
    );
  });
};

const apiEndpoints = build => ({
  get: build.query({
    query: _id => `v1/employee/${_id}`,
    providesTags: (_result, _error, _id) => [{ type: 'Employee', _id }],
  }),
  getByUserId: build.query({
    query: userId => `v1/employee/user/${userId}`,
    providesTags: result => [{ type: 'Employee', _id: result?._id }],
  }),
  getNames: build.query({
    query: body => ({
      url: 'v1/employee/names',
      method: 'POST',
      body,
    }),
  }),
  listVisible: build.query({
    query: ({ search = '', skip = 0, limit = 99, order = 'name', filters, team, group } = {}) => ({
      url: `v1/company/current/visible-employees${
        search ? `?search=${search}&` : '?'
      }skip=${skip}&limit=${limit}&order=${order}&${
        filters ? `filters=${JSON.stringify(filters)}` : ''
      }${team ? `&team=${team}` : ''}${group ? `&group=${group}` : ''}`,
      method: 'GET',
    }),
    transformResponse: response => response.data,
    providesTags: result =>
      result?.employees
        ? [
            ...result.employees.map(({ _id }) => ({ type: 'Employee', _id })),
            { type: 'Employee', _id: 'LIST' },
          ]
        : [{ type: 'Employee', _id: 'LIST' }],
  }),
  list: build.query({
    query: ({
      companyId,
      search = '',
      skip = 0,
      limit = 10,
      order = 'name',
      filters,
      team,
      addFields,
      excludeApps,
    }) => {
      const params = new URLSearchParams({
        skip: skip.toString(),
        limit: limit.toString(),
        order,
      });

      if (filters) params.append('filters', JSON.stringify(filters));
      if (search) params.append('search', search);
      if (team) params.append('team', team);
      if (addFields) addFields.forEach(field => params.append('addFields', field));
      if (excludeApps) params.append('excludeApps', 'true');

      return {
        url: `v1/company/${companyId}/employees?${params.toString()}`,
        method: 'GET',
      };
    },
    transformResponse: response => response.data.employees,
    providesTags: result =>
      result?.data
        ? [
            ...result.data.map(({ _id }) => ({ type: 'Employee', _id })),
            { type: 'Employee', _id: 'LIST' },
          ]
        : [{ type: 'Employee', _id: 'LIST' }],
  }),
  block: build.mutation({
    query: ({ _id, isActive }) => ({
      url: `v1/employee/${_id}/block/${isActive}`,
      method: 'PATCH',
    }),
    async onQueryStarted({ _id, isActive }, { dispatch, getState, queryFulfilled }) {
      const arr = api.util.selectInvalidatedBy(getState(), [{ type: 'Employee', _id }]);
      const patchResults = arr.map(item =>
        dispatch(
          api.util.updateQueryData(item.endpointName, item.originalArgs, draft => {
            let found = null;
            if (typeof draft.data?.find === 'function') {
              found = draft.data?.find(employee => employee._id === _id);
            } else if (typeof draft.employees?.find === 'function') {
              found = draft.employees?.find(employee => employee._id === _id);
            } else if (typeof draft.employee === 'object') {
              found = draft.employee;
            }
            if (found) found.isActive = isActive;
          }),
        ),
      );
      try {
        await queryFulfilled;
      } catch {
        patchResults.forEach(patchResult => patchResult.undo());
      }
    },
  }),
  delete: build.mutation({
    query: ({ _id }) => ({
      url: `v1/employee/${_id}`,
      method: 'DELETE',
    }),
    invalidatesTags: [{ type: 'Employee', _id: 'LIST' }],
  }),
  releasePhone: build.mutation({
    query: ({ _id }) => ({
      url: `v1/employee/${_id}/release-phone`,
      method: 'POST',
    }),
    invalidatesTags: (_result, _error, { _id }) => [
      { type: 'Employee', _id },
      { type: 'Employee', _id: 'LIST' },
    ],
  }),
  downloadOmmnioSignSignedZip: build.mutation({
    query: ({ _id }) => ({
      url: `v1/employee/${_id}/ommniosign-signed-documents-zip`,
      method: 'GET',
      cache: 'no-store',
      responseHandler: async response => {
        const blob = await response.blob();
        const contentDisposition = response.headers.get('Content-Disposition');
        return { blob, contentDisposition };
      },
    }),
  }),
  update: build.mutation({
    query: ({ _id, ...body }) => {
      return {
        url: `v1/employee/${_id}`,
        method: 'PATCH',
        body: Employee.createForm(body),
      };
    },
    async onQueryStarted({ _id, ...body }, { dispatch, getState, queryFulfilled }) {
      // Select the cache entries that would be invalidated by this update.
      const cacheEntries = api.util.selectInvalidatedBy(getState(), [{ type: 'Employee', _id }]);

      // Optimistically update these entries.
      const patchResults = cacheEntries.map(item =>
        dispatch(
          api.util.updateQueryData(item.endpointName, item.originalArgs, draft => {
            let found = null;
            if (typeof draft.data?.find === 'function') {
              found = draft.data?.find(employee => employee._id === _id);
            } else if (typeof draft.employees?.find === 'function') {
              found = draft.employees?.find(employee => employee._id === _id);
            } else if (typeof draft.employee === 'object') {
              found = draft.employee;
            }
            if (found) {
              // PATCH /employee never updates avatar (identity worker does).
              const patchBody = { ...body };
              delete patchBody.avatar;
              Object.assign(found, patchBody);
            }
          }),
        ),
      );
      try {
        // Await for the query to be fulfilled to confirm changes
        const { data: serverData } = await queryFulfilled;
        // Update the cache with the server's response data.
        cacheEntries.forEach(item => {
          dispatch(
            api.util.updateQueryData(item.endpointName, item.originalArgs, draft => {
              const employee = findEmployeeInDraft(draft, _id);
              if (employee && serverData.data?.employee) {
                const previousAvatar = employee.avatar;
                const { avatar: _serverAvatar, ...employeePatch } = serverData.data.employee;
                Object.assign(employee, employeePatch);
                if (!Object.prototype.hasOwnProperty.call(body, 'avatar') && previousAvatar) {
                  employee.avatar = previousAvatar;
                } else if (_serverAvatar) {
                  employee.avatar = _serverAvatar;
                }
              }
            }),
          );
        });
      } catch {
        // Undo optimistic updates if the mutation fails
        patchResults.forEach(patchResult => patchResult.undo());
      }
    },
  }),
  updateSettings: build.mutation({
    query: ({ _id, settings }) => ({
      url: `v1/employee/${_id}/settings`,
      method: 'PATCH',
      body: settings,
    }),
    async onQueryStarted({ _id, settings }, { dispatch, getState, queryFulfilled }) {
      const arr = api.util.selectInvalidatedBy(getState(), [{ type: 'Employee', _id }]);
      const patchResults = arr.map(item =>
        dispatch(
          api.util.updateQueryData(item.endpointName, item.originalArgs, draft => {
            let found = null;
            if (typeof draft.data?.find === 'function') {
              found = draft.data?.find(employee => employee._id === _id);
            } else if (typeof draft.employees?.find === 'function') {
              found = draft.employees?.find(employee => employee._id === _id);
            } else if (typeof draft.employee === 'object') {
              found = draft.employee;
            }
            if (found) {
              found.settings = settings;
            }
          }),
        ),
      );
      try {
        await queryFulfilled;
      } catch {
        patchResults.forEach(patchResult => patchResult.undo());
      }
    },
  }),
  create: build.mutation({
    query: body => ({
      url: 'v1/employee',
      method: 'POST',
      body: Employee.createForm(body),
    }),
    invalidatesTags: [{ type: 'Employee', _id: 'LIST' }],
  }),
  invitePreview: build.query({
    query: ({ companyId, search = '', filters }) => ({
      url: `v1/company/${companyId}/employees/invite-preview${
        search ? `?search=${search}&` : '?'
      }filters=${JSON.stringify(filters)}`,
      method: 'GET',
    }),
    transformResponse: response => response.data,
  }),
  invite: build.mutation({
    query: ({ companyId, search = '', filters }) => ({
      url: `v1/company/${companyId}/employees/invite`,
      method: 'POST',
      body: { search, filters },
    }),
    transformResponse: response => response.data,
  }),
  getSearchValues: build.query({
    query: ({ companyId, field, q = '', skip = 0, limit = 50 }) => {
      const params = new URLSearchParams({ field });
      if (q) params.set('q', q);
      if (skip) params.set('skip', String(skip));
      if (limit) params.set('limit', String(limit));
      return {
        url: `v1/company/${companyId}/employees/search-values?${params.toString()}`,
        method: 'GET',
      };
    },
    transformResponse: response => {
      const { data } = response;
      const values = Array.isArray(data) ? data : (data?.values ?? []);
      const hasMore = data?.hasMore ?? false;
      return { values, hasMore };
    },
  }),
});

api = createApi({
  reducerPath: 'employeesApi',
  baseQuery,
  tagTypes: ['Employee'],
  initialState,
  endpoints: build => apiEndpoints(build),
  keepUnusedDataFor: 30,
});

export const {
  useBlockMutation,
  useDeleteMutation,
  useReleasePhoneMutation,
  useDownloadOmmnioSignSignedZipMutation,
  useGetSearchValuesQuery,
  useLazyGetNamesQuery,
  useGetQuery,
  useGetByUserIdQuery,
  useLazyGetQuery,
  useInviteMutation,
  useInvitePreviewQuery,
  useListQuery,
  useLazyListQuery,
  useListVisibleQuery,
  useLazyListVisibleQuery,
  useUpdateSettingsMutation,
  endpoints: employeesApiEndpoints,
  middleware: employeesApiMiddleware,
  reducer: employeesApiReducer,
  util: { invalidateTags: invalidateEmployeesTags },
} = api;
